import { IanaTimezone } from "./iana-timezone";

/*
 * Names for a zone as people read them, in Australian English — the fleet's own audience. Both are
 * taken at a moment, so a zone reads as standard or daylight time as it was then: Sydney is AEST
 * in July and AEDT in October.
 */

function zoneName(timezone: string, at: Date, style: "short" | "long"): string | undefined {
  if (!IanaTimezone.safeParse(timezone).success) return undefined;
  return new Intl.DateTimeFormat("en-AU", { timeZone: timezone, timeZoneName: style })
    .formatToParts(at)
    .find((part) => part.type === "timeZoneName")?.value;
}

/**
 * The zone's abbreviation, such as `AWST` or `AEDT`.
 *
 * `undefined` where there is none — `Intl` has abbreviations only for some zones (in Australian
 * English, Australia's and New Zealand's) and otherwise gives an offset such as `GMT+8`, which is
 * not a name and is left to `offsetLabel`.
 */
export function timezoneAbbreviation(timezone: string, at: Date): string | undefined {
  const name = zoneName(timezone, at, "short");
  return name === undefined || /^(GMT|UTC)/.test(name) ? undefined : name;
}

/** The zone's full name, such as `Singapore Standard Time` or `British Summer Time`. */
export function timezoneLongName(timezone: string, at: Date): string | undefined {
  return zoneName(timezone, at, "long");
}

/** `Intl.Locale`'s zone data: `getTimeZones()` in newer runtimes, the `timeZones` getter in older ones. */
interface LocaleZones {
  readonly getTimeZones?: () => unknown;
  readonly timeZones?: unknown;
}

const hasZoneData = (locale: Intl.Locale): locale is Intl.Locale & LocaleZones =>
  "getTimeZones" in locale || "timeZones" in locale;

/** The zones `Intl` places in a locale's region; none on a runtime without the data. */
function zonesOf(locale: Intl.Locale): readonly string[] {
  if (!hasZoneData(locale)) return [];
  const read = locale.getTimeZones?.() ?? locale.timeZones;
  return Array.isArray(read) ? read.filter((zone): zone is string => typeof zone === "string") : [];
}

let countries: ReadonlyMap<string, string> | undefined;

/**
 * Every zone's country, from `Intl`'s own region data — built once, on first use.
 *
 * Read per country rather than from a timezone library, which groups zones by shared rules and so
 * puts Accra in Côte d'Ivoire alongside Abidjan. Empty on a runtime without `Intl.Locale`'s zone
 * data (Firefox, as of writing), where zones are shown without a country.
 */
function countryIndex(): ReadonlyMap<string, string> {
  if (countries !== undefined) return countries;
  const names = new Intl.DisplayNames(["en-AU"], { type: "region", fallback: "none" });
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const index = new Map<string, string>();
  for (const first of letters) {
    for (const second of letters) {
      const name = names.of(`${first}${second}`);
      if (name === undefined) continue;
      for (const zone of zonesOf(new Intl.Locale(`und-${first}${second}`))) {
        if (!index.has(zone)) index.set(zone, name);
      }
    }
  }
  countries = index;
  return index;
}

/** The country `timezone` is in, such as `New Zealand`; `undefined` for UTC, or where it is not known. */
export function timezoneCountry(timezone: string): string | undefined {
  return countryIndex().get(timezone);
}

/** `timezone`'s offset at `at` in hours and minutes, such as `UTC+13:00` or `UTC-03:30`. */
export function utcOffset(timezone: string, at: Date): string | undefined {
  if (!IanaTimezone.safeParse(timezone).success) return undefined;
  const offset = new Intl.DateTimeFormat("en-US", { timeZone: timezone, timeZoneName: "longOffset" })
    .formatToParts(at)
    .find((part) => part.type === "timeZoneName")?.value;
  // `longOffset` gives "GMT+13:00", or a bare "GMT" at zero.
  return (
    offset === undefined ? undefined
    : offset === "GMT" ? "UTC+00:00"
    : offset.replace("GMT", "UTC")
  );
}
